import { useQuery } from '@tanstack/react-query';
import {
    GridCallbackDetails,
    GridColDef,
    GridPaginationModel,
    GridRowParams,
    GridSortModel,
} from '@mui/x-data-grid';

import {
    DataGrid,
    DataGridCard,
    dataGridNestedObjectValueGetter,
} from '@barboxx/admin-components';
import {
    ACTIVE_WIDTH,
    DISPLAY_ORDER_WIDTH,
    NAME_MIN_WIDTH,
    PRICE_TIER_WIDTH,
    useBusinessSelector,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { BusinessServingContainer, ServingContainer } from '@barboxx/models';

type Columns = GridColDef<BusinessServingContainer.Base>[];
const COLUMNS: Columns = [
    {
        field: 'displayOrder',
        headerName: 'Display Order',
        width: DISPLAY_ORDER_WIDTH,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'isActive',
        headerName: 'Active',
        width: ACTIVE_WIDTH,
        editable: true,
        type: 'boolean',
        align: 'center',
        headerAlign: 'center',
    },
];

interface Props {
    data: BusinessServingContainer[];
    size: number;
    page: number;
    total: number;
    onRowClick: (id: string) => void;
    onChangeSort: (sort: GridSortModel) => void;
    handleChangePageModel: (
        model: GridPaginationModel,
        details: GridCallbackDetails
    ) => void;
}

export function BusinessServingContainerTable(props: Props) {
    const {
        data = [],
        total,
        size,
        page,
        onRowClick,
        handleChangePageModel,
        onChangeSort,
    } = props;

    const { businessList } = useBusinessSelector();

    const servingContainersQuery = useQuery({
        queryKey: ['servingContainers'],
        queryFn: () => Api.Servings.ServingContainer.getServingContainers(),
    });

    const handleRowClick = (param: GridRowParams) => {
        onRowClick(param.id as string);
    };

    const columns: Columns = [
        {
            field: 'businessId',
            headerName: 'Business',
            width: NAME_MIN_WIDTH,
            type: 'singleSelect',
            valueOptions(params) {
                return (
                    businessList.map(business => ({
                        value: business.id,
                        label: business.place.name,
                    })) ?? []
                );
            },
        },
        {
            field: 'servingContainerId',
            headerName: 'Container',
            editable: true,
            width: PRICE_TIER_WIDTH,
            valueGetter: dataGridNestedObjectValueGetter,
            type: 'singleSelect',
            flex: 1,
            valueOptions(params) {
                return (
                    servingContainersQuery.data?.data?.results.map(
                        (category: ServingContainer) => ({
                            value: category.id,
                            label: category.name,
                        })
                    ) ?? []
                );
            },
        },
        ...COLUMNS,
    ];

    return (
        <DataGridCard title="Business Serving Containers">
            <DataGrid
                rows={data}
                columns={columns}
                paginationModel={{
                    page,
                    pageSize: size,
                }}
                onPaginationModelChange={handleChangePageModel}
                rowCount={total}
                disableColumnMenu
                onRowClick={handleRowClick}
                onSortModelChange={onChangeSort}
            />
        </DataGridCard>
    );
}
